import { ApolloProvider } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { browserTracingIntegration, replayIntegration } from '@sentry/react';
import { TreeTranslationsData } from '@tolgee/core';
import { DevTools, FormatSimple, Tolgee, TolgeeProvider } from '@tolgee/react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { pdfjs } from 'react-pdf';
import { RouterProvider } from 'react-router-dom';
import { Provider } from './components/ui/provider.tsx';
import globalRouter from './global-router.tsx';
import './index.css';
import './utils/analytics.ts';
import client from './utils/client.ts';
import { initializeTemplateSystem } from './utils/template-loader.ts';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.js', import.meta.url).toString();

const sentryDns: string | null = import.meta.env.VITE_SENTRY_DNS as string;
if (sentryDns) {
  Sentry.init({
    dsn: sentryDns,
    integrations: [browserTracingIntegration(), replayIntegration()],
    // Performance Monitoring
    tracesSampleRate: import.meta.env.PROD ? 1.0 : 0.2,
    // Session Replay
    replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 1,
    replaysOnErrorSampleRate: 1.0,
  });

  console.log('Sentry initialized');
}

const tolgee = Tolgee()
  .use(DevTools())
  .use(FormatSimple())
  .init({
    language: localStorage.getItem('language') ?? 'de',
    // for development
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    apiUrl: import.meta.env.VITE_APP_TOLGEE_API_URL,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    apiKey: import.meta.env.VITE_APP_TOLGEE_API_KEY,

    // for production
    staticData: {
      en: () => import('./i18n/en.json').then((m) => m.default as TreeTranslationsData),
      de: () => import('./i18n/de.json').then((m) => m.default as TreeTranslationsData),
      vi: () => import('./i18n/vi.json').then((m) => m.default as TreeTranslationsData),
      pl: () => import('./i18n/pl.json').then((m) => m.default as TreeTranslationsData),
    },
  });

const rootElement = document.getElementById('root');
if (rootElement) {
  // Initialize template system BEFORE rendering React - ensures data-template attribute is set
  initializeTemplateSystem()
    .then(() => {
      console.log('Template system initialized');

      // Render React app after template is loaded
      ReactDOM.createRoot(rootElement).render(
        <React.StrictMode>
          <TolgeeProvider tolgee={tolgee}>
            <ApolloProvider client={client}>
              <Provider>
                <RouterProvider router={globalRouter} />
              </Provider>
            </ApolloProvider>
          </TolgeeProvider>
        </React.StrictMode>
      );
    })
    .catch((error) => {
      console.error('Failed to initialize template system:', error);
      // Still render the app even if template fails
      ReactDOM.createRoot(rootElement).render(
        <React.StrictMode>
          <TolgeeProvider tolgee={tolgee}>
            <ApolloProvider client={client}>
              <Provider>
                <RouterProvider router={globalRouter} />
              </Provider>
            </ApolloProvider>
          </TolgeeProvider>
        </React.StrictMode>
      );
    });
} else {
  console.error('Element with ID "root" not found');
}
